/**
 * The Account's API_Keys, listed (Feature: terraform-provider, task 7.3; Requirements 11.6, 11.7;
 * Design: §Components 9).
 *
 * Built on the `checks-table` conventions the members and checks lists use. Three things about this
 * one are specific to a credential listing and are worth stating, because each is a decision that
 * looks like an omission:
 *
 *  - **Rows are in the API's order, and nothing is filtered out.** `GET /v1/api-keys` returns issuance
 *    order, oldest first, in every state — the store filters on neither state nor expiry, deliberately
 *    (services Req 11.3). A revoked or expired key must stay visible because it stays *auditable*: the
 *    question "what credentials has this Account ever had" is not answerable from a list that hides the
 *    dead ones. So this component sorts nothing and drops nothing.
 *  - **"Last used" is rendered from an absence.** `lastUsedAtEpoch` is omitted until first use rather
 *    than sent as `null` (services Req 11.9), so "never" is the absence itself. Coalescing it to `0`
 *    would print the epoch and coalescing it to `null` would print a blank cell; both would claim a key
 *    had been used. The column says "last used" rather than "last request" because the stored value is
 *    only accurate to within the store's coarsening interval.
 *  - **Rotate stays available on a revoked key.** It looks like a bug and is the opposite: the API
 *    refuses that rotation with a `409` and its own message, and Requirement 11.7 requires the dashboard
 *    to *surface* that refusal. A locally disabled button would make the platform's own explanation
 *    unreachable, and would put a second rule about revocation finality in the SPA — where it could
 *    drift from the one the server enforces.
 */

import type { ApiKeyDto } from '../api/types';
import { formatEpochSeconds } from '../lib/format';

/**
 * What the state cell says, which is **not** always `key.state`.
 *
 * Expiry is a timestamp, not a wire state: `issueApiKey` writes `active` and only revocation moves it
 * (`identity/api-keys.ts`), so a key past its `expiresAtEpoch` is still `active` on the wire. Printing
 * `active` for a credential that stopped working is precisely the misreading this page exists to
 * prevent — an admin looking at why a pipeline started failing would take it as "the key is fine".
 *
 * Revocation is checked first because it is terminal and it is the actionable fact: a revoked key
 * cannot be rotated at all, whereas an expired one can be. The same derive-don't-read rule
 * `MembersPage` applies to a stale invitation, for the same reason — there is no `expired` on the wire.
 */
export function apiKeyDisplayState(key: ApiKeyDto, nowEpoch: number): string {
  if (key.state === 'revoked') return 'revoked';
  if (key.expiresAtEpoch <= nowEpoch) return 'expired';
  return key.state;
}

export interface ApiKeysTableProps {
  /** The Account's keys, exactly as the API returned them. Not sorted, not filtered (Req 11.6). */
  keys: readonly ApiKeyDto[];
  /**
   * Now, in epoch **seconds**, for the derived `expired` state. Injected rather than read from the
   * clock inside the row so a test can pin the boundary instead of racing it; defaults to the clock.
   */
  nowEpoch?: number;
  onRotate: (key: ApiKeyDto) => void;
  onRevoke: (key: ApiKeyDto) => void;
}

export function ApiKeysTable({
  keys,
  nowEpoch = Math.floor(Date.now() / 1000),
  onRotate,
  onRevoke,
}: ApiKeysTableProps): JSX.Element {
  if (keys.length === 0) {
    return <p className="muted">This account has no API keys.</p>;
  }

  return (
    <table className="checks-table" aria-label="API keys">
      <thead>
        <tr>
          <th scope="col">Name</th>
          <th scope="col">Role</th>
          <th scope="col">Created</th>
          <th scope="col">Expires</th>
          <th scope="col">Last used</th>
          <th scope="col">State</th>
          <th scope="col">Actions</th>
        </tr>
      </thead>
      <tbody>
        {keys.map((key) => (
          <tr key={key.keyId}>
            <td>{key.name}</td>
            <td>{key.role}</td>
            <td>{formatEpochSeconds(key.createdAtEpoch)}</td>
            <td>{formatEpochSeconds(key.expiresAtEpoch)}</td>
            {/* The absence itself, not a sentinel — see the module doc. */}
            <td>{key.lastUsedAtEpoch === undefined ? 'never' : formatEpochSeconds(key.lastUsedAtEpoch)}</td>
            <td>{apiKeyDisplayState(key, nowEpoch)}</td>
            <td className="row-actions">
              <button
                type="button"
                aria-label={`Rotate ${key.name}`}
                onClick={() => onRotate(key)}
              >
                Rotate
              </button>
              <button
                type="button"
                className="danger"
                aria-label={`Revoke ${key.name}`}
                onClick={() => onRevoke(key)}
              >
                Revoke
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
